import EmptyDottedIcon from '@assets/icons/empty-dotted.svg';
import i18n from '@/utils/i18n';
import React from 'react';
import { createStyles, useTheme } from '@/contexts/theme-context';
import { ItemDetailCard } from '@/experiences/sales-portal/domain/items/components/item-detail-card';
import { ItemDetailSectionWrapper } from '@/experiences/sales-portal/domain/items/components/item-detail-section-wrapper';
import { PurchaseHistoryCard } from '@/experiences/sales-portal/domain/items/components/purchase-history/purchase-history-card';
import { PurchaseHistoryEntry } from '@/experiences/sales-portal/domain/items/blocs/types/item-detail-content';
import { Typography } from '@usepepper/ui/atoms/typography';
import { View } from 'react-native';

const EMPTY_ICON_SIZE = 32;

export interface PurchaseHistoryListProps {
  entries: PurchaseHistoryEntry[];
  hideGpField: boolean;
}

/**
 * Recent purchase history section. Renders an empty-state card when there
 * are no entries so the rep knows the absence is intentional (not a loading
 * glitch).
 *
 * @param props - {@link PurchaseHistoryListProps}.
 * @returns The rendered section.
 */
export const PurchaseHistoryList: React.FC<PurchaseHistoryListProps> = ({
  entries,
  hideGpField,
}) => {
  const [styles] = useStyles();
  const theme = useTheme();

  return (
    <ItemDetailSectionWrapper
      title={i18n.t(
        'sales_portal.order_entry.item_detail.purchase_history.title',
      )}
    >
      {entries.length === 0 ? (
        <ItemDetailCard>
          <View style={styles.emptyState}>
            <EmptyDottedIcon
              fill={theme.sk.color.icon.primary}
              height={EMPTY_ICON_SIZE}
              width={EMPTY_ICON_SIZE}
            />
            <Typography
              color={theme.sk.color.text.secondary}
              textAlign="center"
              variant="paragraph-md"
            >
              {i18n.t(
                'sales_portal.order_entry.item_detail.purchase_history.empty',
              )}
            </Typography>
          </View>
        </ItemDetailCard>
      ) : (
        <View style={styles.cards}>
          {entries.map((entry, index) => (
            <PurchaseHistoryCard
              entry={entry}
              hideGpField={hideGpField}
              key={[
                entry.orderId,
                entry.orderDate,
                entry.orderItemId,
                index,
              ].join('-')}
            />
          ))}
        </View>
      )}
    </ItemDetailSectionWrapper>
  );
};

const useStyles = createStyles((theme) => ({
  cards: {
    flexDirection: 'column',
    gap: theme.sk.spacing.spaceSmall,
  },
  emptyState: {
    alignItems: 'center',
    gap: theme.sk.spacing.spaceSmall,
    paddingVertical: theme.sk.spacing.spaceSmall,
  },
}));
